import React from 'react';

import './GuideMarkdown.css';

/**
 * Renderers for the small Markdown subset the Getting started guides use.
 *
 * These live apart from the view so they can be exercised directly: react-markdown
 * ships as ES modules that the test runner cannot load, but these are ordinary
 * components and need no parser to test.
 */

// Articles cross-reference each other using a guide:<id> link target.
export const GUIDE_LINK_PREFIX = 'guide:';

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg'];

/**
 * An image narrower than this, relative to its height, sits beside the copy rather
 * than across the full width. Checked against every image in the migrated guides,
 * this reproduces the placement each one was given originally.
 */
export const BESIDE_MAX_ASPECT_RATIO = 1.5;

/**
 * Allow the guide:<id> scheme through the Markdown renderer's URL sanitiser.
 *
 * The sanitiser drops any protocol it does not recognise, which turned every
 * cross-reference into an empty link. Only this one scheme is added; everything else
 * is still handed to the renderer's own check, so javascript: and friends stay blocked.
 */
export function createGuideUrlTransform(sanitizeUrl: (url: string) => string) {
    return (url: string): string => (url.startsWith(GUIDE_LINK_PREFIX) ? url : sanitizeUrl(url));
}

/** True when a media link points at a video rather than an image. */
export function isVideoSource(source: string): boolean {
    const withoutQuery = source.split('?')[0].toLowerCase();
    return VIDEO_EXTENSIONS.some((extension) => withoutQuery.endsWith(extension));
}

/** True when an image of these proportions belongs beside the copy. */
export function isBesideAspectRatio(width: number, height: number): boolean {
    if (!width || !height) {
        return false;
    }
    return width / height < BESIDE_MAX_ASPECT_RATIO;
}

type GuideFigureProps = {
    src: string;
    alt?: string;
    caption?: string;
};

/**
 * An image with its caption. The pair is a single element so that a figure placed
 * beside the copy keeps its caption with it, instead of letting the text wrap
 * between the two.
 */
export function GuideFigure({ src, alt, caption }: GuideFigureProps) {
    const [isBeside, setIsBeside] = React.useState(false);

    // Natural dimensions are known once the image is decoded, and are already
    // available on mount when it comes from the browser cache.
    const measure = React.useCallback((image: HTMLImageElement | null) => {
        if (image) {
            setIsBeside(isBesideAspectRatio(image.naturalWidth, image.naturalHeight));
        }
    }, []);

    return (
        <figure className={isBeside ? 'guide-figure guide-figure--beside' : 'guide-figure'}>
            <img
                src={src}
                alt={alt ?? ''}
                ref={measure}
                onLoad={(event) => measure(event.currentTarget)}
            />
            {caption && <figcaption>{caption}</figcaption>}
        </figure>
    );
}

/** A video always spans the full width; wrapping copy around a player reads poorly. */
export function GuideVideo({ src, alt, caption }: { src: string; alt?: string; caption?: string }) {
    return (
        <figure className='guide-figure'>
            <video controls src={src} aria-label={alt || caption || undefined} />
            {caption && <figcaption>{caption}</figcaption>}
        </figure>
    );
}

/**
 * Build the component map for an article. Raw HTML stays disabled, so a video is
 * written in the Markdown as an image link and upgraded to a player here, and an
 * image's caption travels as its Markdown title.
 */
export function createGuideMarkdownComponents(onOpenGuide: (itemId: string) => void) {
    return {
        a: ({ href, children }: any) => {
            // Keep cross-references between guides inside the dashboard.
            if (typeof href === 'string' && href.startsWith(GUIDE_LINK_PREFIX)) {
                const targetId = href.slice(GUIDE_LINK_PREFIX.length);
                return (
                    <button type='button' style={styles.inlineLinkButton} onClick={() => onOpenGuide(targetId)}>
                        {children}
                    </button>
                );
            }
            return (
                <a href={href} target='_blank' rel='noreferrer' style={styles.link}>
                    {children}
                </a>
            );
        },
        img: ({ src, alt, title }: any) => {
            const source = typeof src === 'string' ? src : '';
            const caption = typeof title === 'string' && title ? title : undefined;
            if (isVideoSource(source)) {
                return <GuideVideo src={source} alt={alt} caption={caption} />;
            }
            return <GuideFigure src={source} alt={alt} caption={caption} />;
        },
        h2: ({ children }: any) => <h2 style={styles.heading}>{children}</h2>,
        h3: ({ children }: any) => <h3 style={styles.subheading}>{children}</h3>,
        p: ({ children }: any) => <p style={styles.paragraph}>{children}</p>,
        li: ({ children }: any) => <li style={styles.listItem}>{children}</li>,
    };
}

const styles: { [key: string]: React.CSSProperties } = {
    heading: {
        fontSize: '22px',
        fontWeight: 600,
        lineHeight: 1.3,
        color: '#18143d',
        margin: '36px 0 14px',
    },
    subheading: {
        fontSize: '18px',
        fontWeight: 600,
        lineHeight: 1.35,
        color: '#18143d',
        margin: '24px 0 8px',
    },
    paragraph: {
        margin: '0 0 18px',
    },
    listItem: {
        margin: '0 0 8px',
    },
    link: {
        color: '#544ac2',
        textDecoration: 'underline',
    },
    inlineLinkButton: {
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        color: '#544ac2',
        textDecoration: 'underline',
        cursor: 'pointer',
    },
};
